import { calculateDamage, derivedStats, MAX_LEVEL, inHorizontalRange, type DamageResult } from './combat';
import { cooldownAfterHaste, getSkillForSlot, type SkillDefinition } from './skills';
import type { ClassSkill } from './professions';

export type AttackResult = DamageResult & {
  missed: boolean;
  stage: 1 | 2 | 3;
  power: number;
};

export type SkillUseResult = {
  used: boolean;
  skill?: SkillDefinition;
  readyAt: number;
  damage?: DamageResult;
  healed?: number;
};

export type CombatRuntimeConfig = {
  enemyMaxHp?: number;
  enemyDamage?: number;
  level?: number;
};

function clampLevel(level: number): number {
  return Math.max(1, Math.min(MAX_LEVEL, Math.trunc(level)));
}

export class CombatRuntime {
  level: number;
  enemyMaxHp: number;
  enemyDamage: number;
  enemyHp: number;
  playerHp: number;
  mana=100;
  wardUntil=0;
  bonusAtk = 0;
  bonusHp = 0;
  bonusDefense = 0;
  skillRanks = [0,0,0,0];

  constructor(config: CombatRuntimeConfig = {}) {
    this.level = clampLevel(config.level ?? 1);
    this.enemyMaxHp = Math.max(1, Math.round(config.enemyMaxHp ?? 180));
    this.enemyDamage = Math.max(1, Math.round(config.enemyDamage ?? 14));
    this.enemyHp = this.enemyMaxHp;
    this.playerHp = this.playerMaxHp;
  }

  get playerMaxHp(): number {
    return derivedStats(this.level).maxHp + this.bonusHp;
  }

  combo = 0;
  attackStage: 1 | 2 | 3 = 1;
  dodgeReadyAt = 0;
  evadingUntil = 0;
  readonly skillReadyAt = [0, 0, 0, 0];
  readonly classReadyAt:Record<string,number>={};
  cast(skill:ClassSkill,rank:number,now:number):{used:boolean;damage?:number;healed?:number} {
    if(this.playerHp<=0||this.level<skill.level||this.mana<skill.cost||(this.classReadyAt[skill.id]??0)>now)return {used:false};
    this.mana-=skill.cost;
    this.classReadyAt[skill.id]=now+skill.cooldown*1000;
    if(skill.effect==='guard'||skill.effect==='summon'){this.wardUntil=now+(skill.effect==='summon'?5000:skill.duration);return {used:true};}
    const power=skill.power*(1+rank*.12);
    if(skill.effect==='heal'){const before=this.playerHp;this.playerHp=Math.min(this.playerMaxHp,this.playerHp+Math.round(this.playerMaxHp*power));return {used:true,healed:this.playerHp-before};}
    const damage=Math.max(1,Math.round((derivedStats(this.level).atk+this.bonusAtk)*power));this.enemyHp=Math.max(0,this.enemyHp-damage);return {used:true,damage};
  }

  setLevel(level: number): void {
    const beforeMaxHp = this.playerMaxHp;
    this.level = clampLevel(level);
    const afterMaxHp = this.playerMaxHp;
    const gainedMaxHp = Math.max(0, afterMaxHp - beforeMaxHp);
    this.playerHp = this.playerHp <= 0 ? 0 : Math.min(afterMaxHp, this.playerHp + gainedMaxHp);
  }

  configureEnemy(config: Pick<CombatRuntimeConfig, 'enemyMaxHp' | 'enemyDamage'>): void {
    this.enemyMaxHp = Math.max(1, Math.round(config.enemyMaxHp ?? this.enemyMaxHp));
    this.enemyDamage = Math.max(1, Math.round(config.enemyDamage ?? this.enemyDamage));
    this.enemyHp = this.enemyMaxHp;
    this.enemyStunnedUntil = 0;
    this.combo = 0;
    this.attackStage = 1;
  }

  resetEnemy(): void {
    this.enemyHp = this.enemyMaxHp;
    this.enemyStunnedUntil = 0;
    this.combo = 0;
    this.attackStage = 1;
  }

  resetPlayer(): void {
    this.playerHp = this.playerMaxHp;
    this.evadingUntil = 0;
    this.dodgeReadyAt = 0;
  }

  enemyStunnedUntil = 0;

  normalAttack(rngRoll = Math.random(), contactHeight = 47, hitRoll = .5): AttackResult {
    const stage = this.attackStage;
    const power = stage === 1 ? 0.78 : stage === 2 ? 0.92 : 1.42;
    this.combo += 1;
    this.attackStage = stage === 3 ? 1 : ((stage + 1) as 1 | 2 | 3);
    if(hitRoll>=.94){this.combo=0;return {damage:0,critical:false,missed:true,stage,power};}
    const result = calculateDamage(this.level, power, this.combo, rngRoll);
    result.damage += Math.round(this.bonusAtk * power * (result.critical ? 1.5 : 1));
    result.damage = Math.max(1, Math.round(result.damage * (contactHeight >= 95 ? 1.5 : contactHeight < 35 ? .75 : 1)));
    this.enemyHp = Math.max(0, this.enemyHp - result.damage);
    this.attackStage = stage === 3 ? 1 : ((stage + 1) as 1 | 2 | 3);
    return { ...result, missed:false, stage, power };
  }

  canDodge(now: number): boolean {
    return now >= this.dodgeReadyAt;
  }

  beginDodge(now: number): boolean {
    if (!this.canDodge(now)) return false;
    this.dodgeReadyAt = now + 900;
    this.evadingUntil = now + 260;
    return true;
  }

  isEvading(now: number): boolean {
    return now < this.evadingUntil;
  }

  canUseSkill(slot: number, now: number): boolean {
    return slot >= 0 && slot < 4 && now >= (this.skillReadyAt[slot] ?? Number.POSITIVE_INFINITY);
  }

  useSkill(slot: number, now: number, rngRoll = Math.random()): SkillUseResult {
    const skill = getSkillForSlot(slot);
    if (!skill || !this.canUseSkill(slot, now)) {
      return { used: false, readyAt: this.skillReadyAt[slot] ?? now };
    }

    const stats = derivedStats(this.level);
    const readyAt = now + cooldownAfterHaste(skill, stats.haste) * 1000;
    this.skillReadyAt[slot] = readyAt;

    if (skill.effect === 'heal') {
      const healed = Math.max(18, Math.round(this.playerMaxHp * 0.18 * (1 + (this.skillRanks[slot] ?? 0) * .12)));
      this.playerHp = Math.min(this.playerMaxHp, this.playerHp + healed);
      return { used: true, skill, readyAt, healed };
    }

    const damage = calculateDamage(this.level, skill.power, this.combo, rngRoll);
    damage.damage = Math.round((damage.damage + this.bonusAtk * skill.power * (damage.critical ? 1.5 : 1)) * (1 + (this.skillRanks[slot] ?? 0) * .12));
    this.enemyHp = Math.max(0, this.enemyHp - damage.damage);
    return { used: true, skill, readyAt, damage };
  }

  remainingSkillCooldown(slot: number, now: number): number {
    return Math.max(0, ((this.skillReadyAt[slot] ?? 0) - now) / 1000);
  }

  remainingDodgeCooldown(now: number): number {
    return Math.max(0, (this.dodgeReadyAt - now) / 1000);
  }

  resolveEnemyStrike(
    now: number,
    playerX: number,
    enemyX: number,
    enemyFacing: -1 | 1,
    attackRange = 120,
  ): { hit: boolean; damage: number } {
    if (now < this.enemyStunnedUntil || this.isEvading(now) || !inHorizontalRange(enemyX, playerX, enemyFacing, attackRange)) {
      return { hit: false, damage: 0 };
    }
    const damage = Math.max(1, this.enemyDamage - this.bonusDefense - (now<this.wardUntil?12:0));
    this.playerHp = Math.max(0, this.playerHp - damage);
    return { hit: true, damage };
  }
}
